import { Component, OnInit, HostListener, ElementRef, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { ModalComponent } from '../../primeng/modal/modal.component';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { ToasterService } from '../../../../services/toaster.service';
import { StepperComponent, StepperStep } from '../../primeng';
import { CatalogueApiService, AuthApiService, FileApiService } from '@angular-workspace/services';
import {
  buildTanuhDatasetItemPayload,
  extractCreatedItemId,
} from '../../../utils/tanuh-dataset-item-payload.util';
import { EncryptedUploadService } from '../../../services/encrypted-upload.service';
import { environment } from '../../../../../environments/environment';
import { buildFileTreeRows, collectFolderKeys, FileTreeRow, readDroppedEntries } from '../../../utils/file-tree.util';

type UploadedFileItem = {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  previewData?: any;
  uploadId?: string;
  key?: string;
};

@Component({
  selector: 'app-provider-upload',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    ModalComponent,
    ConfirmationDialogComponent,
    StepperComponent,
  ],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
})
export class ProviderUploadComponent implements OnInit, AfterViewInit {

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  currentStep = 3; // Step 4 (0-based index) - Upload files; adjusted in ngOnInit for dataset enter flow
  totalSteps = 4;

  @ViewChild('step4Template', { static: true }) step4Template!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [
    {
      key: 'step1',
      label: 'Setup your datasets',
    },
    {
      key: 'step2',
      label: 'Enter Metadata',
    },
    {
      key: 'step3',
      label: 'Additional Info',
    },
    {
      key: 'step4',
      label: 'Upload files',
    },
  ];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.isModel() ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => (i === 0 ? { ...step, label } : step));
  }

  isModel(): boolean {
    return this.router.url.includes('/add-model');
  }

  isDatasetEnterFlow(): boolean {
    return !this.isModel() && !this.isUploadMetadataFlow();
  }

  /**
   * Only models still use the upload-metadata → preview-metadata path. For datasets an uploaded
   * Croissant file pre-fills the builder, so their steps are always Enter Metadata → Additional Metadata.
   */
  private isUploadMetadataFlow(): boolean {
    if (!this.isModel()) {
      return false;
    }
    const savedData = localStorage.getItem(this.getLocalStorageKey());
    if (!savedData) {
      return false;
    }
    try {
      const data = JSON.parse(savedData);
      return data.metadataOption === 'upload';
    } catch {
      return false;
    }
  }

  private getRouteBase(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/org-admin/add-model')) {
      return 'org-admin/add-model';
    }
    if (currentUrl.includes('/org-admin/add-dataset')) {
      return 'org-admin/add-dataset';
    }
    if (currentUrl.includes('/add-model/')) {
      return 'provider/add-contributions/add-model';
    }
    if (currentUrl.includes('/add-contributions/dataset')) {
      return 'provider/add-contributions/dataset';
    }
    return 'provider/add-contributions/add-dataset';
  }

  private getUploadStepContentIndex(): number {
    return 3;
  }

  selectedUploadOption = 'upload-file';
  selectedFiles: File[] = [];
  private filePaths = new WeakMap<File, string>();

  // File tree view state
  private collapsedFolders = new Set<string>();
  private treeRowsCache: Array<FileTreeRow<File, UploadedFileItem>> = [];
  private treeCacheSelected: File[] = [];
  private treeCacheUploaded: Array<UploadedFileItem> = [];
  private treeCacheVersion = -1;
  private treeVersion = 0;
  uploadedFiles: Array<{
    file: File;
    progress: number;
    status: 'pending' | 'uploading' | 'completed' | 'error';
    error?: string;
    previewData?: any;
    uploadId?: string;
    key?: string;
  }> = [];

  // Ground truth (.json / .csv) — single-file upload via the same multipart flow
  groundTruthItem: typeof this.uploadedFiles[0] | null = null;

  // Preview modal state
  previewModalOpen = false;
  previewingFile: any = null;
  uploading = false;
  dragActive = false;
  isUploadSuccessModalOpen = false;
  isPublishSuccessModalOpen = false;
  isDirectPushSuccessModalOpen = false; // Track Direct Push success modal state
  isPublishing = false;
  isPublished = false; // Track if publish was successful
  createdDatasetId: string | null = null; // Store the created dataset ID for navigation
  requestType: 'SFTP' | 'API' | null = null; // Track the type of request (SFTP or API Integration)
  
  // Published files preview
  publishedFilesPreview: { headers: string[], rows: any[][] } | null = null;
  sftpForm!: FormGroup;
  apiIntegrationForm!: FormGroup;

  // Dropdown options
  httpMethodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
  authenticationTypeOptions = ['Bearer Token', 'API Key', 'Basic Auth', 'OAuth 2.0', 'Custom'];
  responseFormatOptions = ['JSON', 'XML', 'CSV', 'Text'];

  // Dropdown states
  isHttpMethodDropdownOpen = false;
  isAuthenticationTypeDropdownOpen = false;
  isResponseFormatDropdownOpen = false;

  uploadOptions = [
    {
      value: 'upload-file',
      label: 'Upload File',
      description: 'Upload your data files directly through the web interface. Supports various file formats including CSV, JSON, and Excel.',
      icon: 'upload'
    },
  ];

  isCreatingItem = false; // Flag to prevent multiple API calls

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private toasterService: ToasterService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private catalogueApiService: CatalogueApiService,
    private authApiService: AuthApiService,
    private fileApiService: FileApiService,
    private encryptedUploadService: EncryptedUploadService
  ) {}

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  private getFileExtension(name: string): string {
    const i = name.lastIndexOf('.');
    return i >= 0 ? '.' + name.slice(i + 1).toLowerCase() : '';
  }

  ngOnInit(): void {
    // Initialize SFTP form
    this.sftpForm = this.fb.group({
      sftpHost: ['', [Validators.required]],
      port: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
      remotePath: ['', [Validators.required]],
      localFilePath: ['', [Validators.required]],
      optionalNotes: ['']
    });

    // Initialize API Integration form
    this.apiIntegrationForm = this.fb.group({
      dataSourceName: ['', [Validators.required]],
      apiBaseUrl: ['', [Validators.required]],
      endpointPath: ['', [Validators.required]],
      httpMethod: ['', [Validators.required]],
      customHeaders: [''],
      queryParameters: [''],
      bodyPayload: [''],
      authenticationDetails: [''],
      authenticationType: ['', [Validators.required]],
      responseFormat: ['', [Validators.required]],
      dataPathInResponse: ['', [Validators.required]],
      expectedPullFrequency: ['', [Validators.required]],
      additionalNotes: ['']
    });

    // Update stepper step label based on metadata flow
    this.updateStepperSteps();
    
    // Initialize createdDatasetId from localStorage if it exists
    this.initializeCreatedItemId();
  }

  /**
   * Initialize createdDatasetId from localStorage if item was already created
   */
  private initializeCreatedItemId(): void {
    const localStorageKey = this.getLocalStorageKey();
    const savedData = localStorage.getItem(localStorageKey);
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const itemId = parsed.itemId || parsed.id || parsed.item_id;
        if (itemId && !this.createdDatasetId) {
          this.createdDatasetId = itemId;
          console.log('Initialized createdDatasetId from localStorage:', itemId);
        }
      } catch (error) {
        console.error('Error parsing saved data for item ID:', error);
      }
    }
  }

  /**
   * Update stepper steps based on metadata flow (Enter Metadata vs Upload Metadata)
   */
  private updateStepperSteps(): void {
    if (this.isUploadMetadataFlow()) {
      this.stepperSteps = [
        { key: 'step1', label: 'Setup your datasets' },
        { key: 'step2', label: 'Upload metadata' },
        { key: 'step3', label: 'Preview Metadata' },
        { key: 'step4', label: 'Upload files' },
      ];
      this.currentStep = 3;
    } else if (this.isModel()) {
      this.stepperSteps = [
        { key: 'step1', label: 'Setup your Models' },
        { key: 'step2', label: 'Enter Metadata' },
        { key: 'step3', label: 'Additional Info' },
        { key: 'step4', label: 'Upload files' },
      ];
      this.currentStep = 3;
    } else {
      this.stepperSteps = [
        { key: 'step1', label: 'Setup your datasets' },
        { key: 'step2', label: 'Enter Metadata' },
        { key: 'step3', label: 'Additional Metadata' },
        { key: 'step4', label: 'Upload files' },
      ];
      this.currentStep = 3;
    }
  }


  selectUploadOption(option: string): void {
    this.selectedUploadOption = option;
    // Clear files when switching options
    if (option !== 'upload-file') {
      this.selectedFiles = [];
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  getFileSize(size: number): string {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  isUploadFileSelected(): boolean {
    return this.selectedUploadOption === 'upload-file';
  }

  /**
   * Check if an upload option should be disabled
   */
  isUploadOptionDisabled(_optionValue: string): boolean {
    return false;
  }

  /**
   * Check if Publish button should be disabled
   * Disable until files are uploaded (for Upload File option) or forms are valid (for SFTP/API)
   */
  isPublishButtonDisabled(): boolean {
    return this.uploadedFiles.length === 0 || !this.isAllCompleted() || this.groundTruthItem?.status !== 'completed';
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = true;
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  /**
   * Handle drop event
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;

    const items = event.dataTransfer?.items;
    const entries = items
      ? Array.from(items).map(i => (i as any).webkitGetAsEntry?.() as FileSystemEntry | null)
      : [];
    if (entries.length > 0 && entries.every(e => !!e)) {
      // Read entries synchronously-started: DataTransferItems are invalidated after the handler returns
      readDroppedEntries(entries as FileSystemEntry[]).then(dropped => {
        dropped.forEach(d => this.filePaths.set(d.file, d.path));
        this.addFiles(dropped.map(d => d.file));
      });
    } else if (event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  /**
   * Handle file selection from input
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      this.addFiles(files);
      input.value = ''; // Reset input
    }
  }

  /**
   * Object path of a file: relative path inside a chosen/dropped folder (e.g. "images/cats/1.png"),
   * or just the file name for a plain file. This becomes the object name in storage.
   */
  getFilePath(file: File): string {
    return this.filePaths.get(file) || file.webkitRelativePath || file.name;
  }

  /**
   * Add files to the selected files list. Any file type is accepted; folder structure is preserved
   * through each file's relative path.
   */
  private addFiles(files: File[]): void {
    for (const file of files) {
      const path = this.getFilePath(file);
      const exists =
        this.selectedFiles.some(f => this.getFilePath(f) === path && f.size === file.size) ||
        this.uploadedFiles.some(f => this.getFilePath(f.file) === path && f.file.size === file.size);
      if (!exists) {
        this.selectedFiles.push(file);
      }
    }
  }

  // ---------- Overall upload progress ----------

  getTotalFileCount(): number {
    return this.uploadedFiles.length + this.selectedFiles.length;
  }

  getCompletedFileCount(): number {
    return this.uploadedFiles.filter(f => f.status === 'completed').length;
  }

  getFailedFileCount(): number {
    return this.uploadedFiles.filter(f => f.status === 'error').length;
  }

  /** Overall percentage, counting in-flight files by their own progress. */
  getOverallProgressPercent(): number {
    const total = this.getTotalFileCount();
    if (total === 0) return 0;
    const done = this.uploadedFiles.reduce(
      (sum, f) => sum + (f.status === 'completed' ? 100 : f.status === 'uploading' ? f.progress : 0), 0);
    return Math.round(done / total);
  }

  // ---------- File tree ----------

  trackTreeRow(_index: number, row: FileTreeRow<File, UploadedFileItem>): string {
    return row.kind + ':' + row.key;
  }

  hasFolders(): boolean {
    return this.getTreeRows().some(r => r.kind === 'folder');
  }

  toggleFolder(key: string): void {
    if (!this.collapsedFolders.delete(key)) {
      this.collapsedFolders.add(key);
    }
    this.treeVersion++;
  }

  collapseAllFolders(): void {
    this.collectFolderKeys().forEach(k => this.collapsedFolders.add(k));
    this.treeVersion++;
  }

  expandAllFolders(): void {
    this.collapsedFolders.clear();
    this.treeVersion++;
  }

  private collectFolderKeys(): string[] {
    return collectFolderKeys([
      ...this.selectedFiles.map(f => this.getFilePath(f)),
      ...this.uploadedFiles.map(f => this.getFilePath(f.file)),
    ]);
  }

  /** Flattened, currently-visible tree rows. Rebuilt only when the file lists or collapse state change. */
  getTreeRows(): Array<FileTreeRow<File, UploadedFileItem>> {
    const sameSel = this.treeCacheSelected.length === this.selectedFiles.length &&
      this.selectedFiles.every((f, i) => f === this.treeCacheSelected[i]);
    const sameUp = this.treeCacheUploaded.length === this.uploadedFiles.length &&
      this.uploadedFiles.every((f, i) => f === this.treeCacheUploaded[i]);
    if (sameSel && sameUp && this.treeCacheVersion === this.treeVersion) {
      return this.treeRowsCache;
    }
    this.treeRowsCache = buildFileTreeRows<File, UploadedFileItem>(
      this.selectedFiles.map(file => ({ path: this.getFilePath(file), ref: file })),
      this.uploadedFiles.map(item => ({ path: this.getFilePath(item.file), ref: item })),
      this.collapsedFolders,
    );
    this.treeCacheSelected = [...this.selectedFiles];
    this.treeCacheUploaded = [...this.uploadedFiles];
    this.treeCacheVersion = this.treeVersion;
    return this.treeRowsCache;
  }

  isSftpUploadSelected(): boolean {
    return this.selectedUploadOption === 'sftp-upload';
  }

  isApiIntegrationSelected(): boolean {
    return this.selectedUploadOption === 'api-integration';
  }

  isDirectPushSelected(): boolean {
    return this.selectedUploadOption === 'direct-push';
  }

  // Direct Push Code Snippet
  directPushCode = `#!/usr/bin/env python3
"""
Databank File Uploader

A Python script to upload large files to the NHA Files Connect API using multipart upload.
Supports both single file and directory uploads with proper authentication and progress tracking.

Usage:
    Configure the variables at the top of the script and run:
    python databank_uploader.py
"""

import os
import json
import requests
import hashlib
import time
import mimetypes
from typing import Dict, List, Optional, Tuple
from pathlib import Path
from dataclasses import dataclass
from urllib.parse import urljoin
import logging
from datetime import datetime, timedelta

# Configure logging
logging.basicConfig(
    level=logging.INFO,
    format='%(asctime)s - %(levelname)s - %(message)s'
)
logger = logging.getLogger(__name__)

# =============================================================================
# CONFIGURATION VARIABLES - UPDATE THESE
# =============================================================================

# API Configuration
API_BASE_URL = "https://your-api-domain.com"  # Update with your API base URL
API_VERSION = "v1"

# Authentication Configuration
KEYCLOAK_TOKEN_URL = "https://idp.tgdex.telangana.gov.in/auth/realms/tgdex/protocol/openid-connect/token"
USERNAME = "benchmark@tanuh.ai"
PASSWORD = "Sunil@4989"
CLIENT_ID = "angular-client"

# Upload Configuration
DATABANK_ID = "your-databank-id"  # Update with your databank ID

# File/Directory Configuration
# Set either FILE_PATH for single file or DIRECTORY_PATH for directory upload
FILE_PATH = None  # e.g., "/path/to/your/file.csv"
DIRECTORY_PATH = None  # e.g., "/path/to/your/directory"

# Upload Configuration
CHUNK_SIZE = 1024 * 1024  # 1MB chunks (matches API default)
MAX_FILE_SIZE_GB = 1000  # Maximum file size in GB (matches API default)
MAX_RETRIES = 3
RETRY_DELAY = 1  # seconds

# =============================================================================
# DATA CLASSES
# =============================================================================

@dataclass
class AuthToken:
    """Authentication token data"""
    access_token: str
    refresh_token: Optional[str]
    expires_in: int
    token_type: str = "Bearer"
    expires_at: Optional[datetime] = None
    
    def __post_init__(self):
        """Set expiration time after initialization"""
        if self.expires_at is None:
            self.expires_at = datetime.now() + timedelta(seconds=self.expires_in - 60)  # 60 seconds buffer
    
    @property
    def header_value(self) -> str:
        return f"{self.token_type} {self.access_token}"
    
    @property
    def is_expired(self) -> bool:
        """Check if token is expired or about to expire"""
        return datetime.now() >= self.expires_at

@dataclass
class UploadPart:
    """Upload part information"""
    part_number: int
    presigned_url: str
    etag: Optional[str] = None
    uploaded: bool = False

@dataclass
class UploadSession:
    """Upload session information"""
    upload_id: str
    key: str
    parts: List[UploadPart]
    total_parts: int

@dataclass
class FileInfo:
    """File information for upload"""
    path: Path
    size: int
    content_type: str
    relative_path: str  # Path relative to upload directory

# =============================================================================
# UTILITY FUNCTIONS
# =============================================================================

def get_content_type(file_path: Path) -> str:
    """Get content type for a file"""
    content_type, _ = mimetypes.guess_type(str(file_path))
    if content_type is None:
        # Default mappings for common file types
        ext = file_path.suffix.lower()
        content_type_map = {
            '.csv': 'text/csv',
            '.json': 'application/json',
            '.txt': 'text/plain',
            '.parquet': 'application/octet-stream',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            '.zip': 'application/zip'
        }
        content_type = content_type_map.get(ext, 'application/octet-stream')
    return content_type

def calculate_file_parts(file_size: int, chunk_size: int) -> int:
    """Calculate number of parts needed for multipart upload"""
    return (file_size + chunk_size - 1) // chunk_size

def validate_file_size(file_size: int) -> bool:
    """Validate file size against maximum allowed"""
    max_size_bytes = MAX_FILE_SIZE_GB * 1024 * 1024 * 1024
    return file_size <= max_size_bytes

def get_file_info(file_path: Path, base_path: Path = None) -> FileInfo:
    """Get file information"""
    if not file_path.exists():
        raise FileNotFoundError(f"File not found: {file_path}")
    
    if not file_path.is_file():
        raise ValueError(f"Path is not a file: {file_path}")
    
    file_size = file_path.stat().st_size
    
    if not validate_file_size(file_size):
        raise ValueError(f"File size ({file_size / (1024**3):.2f} GB) exceeds maximum allowed ({MAX_FILE_SIZE_GB} GB)")
    
    content_type = get_content_type(file_path)
    
    if base_path:
        relative_path = str(file_path.relative_to(base_path))
    else:
        relative_path = file_path.name
    
    return FileInfo(
        path=file_path,
        size=file_size,
        content_type=content_type,
        relative_path=relative_path
    )

def collect_files_from_directory(directory_path: Path) -> List[FileInfo]:
    """Collect all supported files from directory"""
    if not directory_path.exists():
        raise FileNotFoundError(f"Directory not found: {directory_path}")
    
    if not directory_path.is_dir():
        raise ValueError(f"Path is not a directory: {directory_path}")
    
    files = []
    for file_path in directory_path.rglob('*'):
        if file_path.is_file():
            try:
                file_info = get_file_info(file_path, directory_path)
                files.append(file_info)
            except ValueError as e:
                logger.warning(f"Skipping file {file_path}: {e}")
    
    return files

# =============================================================================
# API CLIENT
# =============================================================================

class DatabankAPIClient:
    """Client for interacting with the Databank API"""
    
    def __init__(self, base_url: str, version: str = "v1"):
        self.base_url = base_url.rstrip('/')
        self.version = version
        self.session = requests.Session()
        self.token: Optional[AuthToken] = None
    
    def _get_api_url(self, endpoint: str) -> str:
        """Get full API URL for an endpoint"""
        return urljoin(f"{self.base_url}/{self.version}/", endpoint.lstrip('/'))
    
    def _make_request(self, method: str, endpoint: str, **kwargs) -> requests.Response:
        """Make authenticated API request"""
        url = self._get_api_url(endpoint)
        headers = kwargs.get('headers', {})
        
        # Check if token needs refresh before making request
        if self.token and self.token.is_expired:
            logger.info("Token expired, refreshing...")
            self._refresh_token()
        
        if self.token:
            headers['Authorization'] = self.token.header_value
        
        kwargs['headers'] = headers
        
        logger.debug(f"{method.upper()} {url}")
        
        for attempt in range(MAX_RETRIES):
            try:
                response = self.session.request(method, url, **kwargs)
                
                if response.status_code == 401:
                    logger.info("Token invalid, attempting refresh...")
                    self._refresh_token()
                    headers['Authorization'] = self.token.header_value
                    kwargs['headers'] = headers
                    response = self.session.request(method, url, **kwargs)
                
                return response
                
            except requests.exceptions.RequestException as e:
                logger.warning(f"Request failed (attempt {attempt + 1}/{MAX_RETRIES}): {e}")
                if attempt < MAX_RETRIES - 1:
                    time.sleep(RETRY_DELAY * (2 ** attempt))
                else:
                    raise
    
    def authenticate(self) -> AuthToken:
        """Authenticate and get access token"""
        logger.info("Authenticating with Keycloak...")
        
        data = {
            'grant_type': 'password',
            'client_id': CLIENT_ID,
            'username': USERNAME,
            'password': PASSWORD
        }
        
        response = requests.post(
            KEYCLOAK_TOKEN_URL,
            data=data,
            headers={'Content-Type': 'application/x-www-form-urlencoded'}
        )
        
        if response.status_code != 200:
            raise Exception(f"Authentication failed: {response.status_code} - {response.text}")
        
        token_data = response.json()
        self.token = AuthToken(
            access_token=token_data['access_token'],
            refresh_token=token_data.get('refresh_token'),
            expires_in=token_data['expires_in'],
            token_type=token_data.get('token_type', 'Bearer')
        )
        
        logger.info("Authentication successful")
        return self.token
    
    def _refresh_token(self) -> AuthToken:
        """Refresh access token using refresh token"""
        if not self.token or not self.token.refresh_token:
            logger.info("No refresh token available, re-authenticating...")
            return self.authenticate()
        
        logger.info("Refreshing access token...")
        
        data = {
            'grant_type': 'refresh_token',
            'client_id': CLIENT_ID,
            'refresh_token': self.token.refresh_token
        }
        
        response = requests.post(
            KEYCLOAK_TOKEN_URL,
            data=data,
            headers={'Content-Type': 'application/x-www-form-urlencoded'}
        )
        
        if response.status_code != 200:
            logger.warning(f"Token refresh failed: {response.status_code} - {response.text}")
            logger.info("Falling back to full authentication...")
            return self.authenticate()
        
        token_data = response.json()
        self.token = AuthToken(
            access_token=token_data['access_token'],
            refresh_token=token_data.get('refresh_token', self.token.refresh_token),
            expires_in=token_data['expires_in'],
            token_type=token_data.get('token_type', 'Bearer')
        )
        
        logger.info("Token refresh successful")
        return self.token
    
    def initiate_upload(self, key: str, num_parts: int, content_type: str, databank_id: str) -> UploadSession:
        """Initiate multipart upload"""
        logger.info(f"Initiating upload: {key} ({num_parts} parts)")
        
        payload = {
            'key': key,
            'numParts': num_parts,
            'contentType': content_type
        }
        
        response = self._make_request(
            'POST',
            f'databanks/{databank_id}/uploads',
            json=payload
        )
        
        if response.status_code != 200:
            raise Exception(f"Failed to initiate upload: {response.status_code} - {response.text}")
        
        data = response.json()
        if not data.get('success', False):
            raise Exception(f"Upload initiation failed: {data.get('error', 'Unknown error')}")
        
        result = data['result']
        parts = [
            UploadPart(part_number=part['partNumber'], presigned_url=part['presignedUrl'])
            for part in result['parts']
        ]
        
        return UploadSession(
            upload_id=result['uploadId'],
            key=result['key'],
            parts=parts,
            total_parts=len(parts)
        )
    
    def upload_part(self, part: UploadPart, data: bytes) -> str:
        """Upload a single part"""
        logger.debug(f"Uploading part {part.part_number}")
        
        response = requests.put(
            part.presigned_url,
            data=data,
            headers={'Content-Type': 'application/octet-stream'}
        )
        
        if response.status_code != 200:
            raise Exception(f"Failed to upload part {part.part_number}: {response.status_code} - {response.text}")
        
        etag = response.headers.get('ETag', '').strip('"')
        part.etag = etag
        part.uploaded = True
        
        return etag
    
    def complete_upload(self, session: UploadSession, databank_id: str) -> Dict:
        """Complete multipart upload"""
        logger.info(f"Completing upload: {session.key}")
        
        parts = [
            {
                'PartNumber': part.part_number,
                'ETag': part.etag
            }
            for part in session.parts if part.uploaded
        ]
        
        payload = {
            'key': session.key,
            'parts': parts
        }
        
        response = self._make_request(
            'PUT',
            f'databanks/{databank_id}/uploads/{session.upload_id}',
            json=payload
        )
        
        if response.status_code != 200:
            raise Exception(f"Failed to complete upload: {response.status_code} - {response.text}")
        
        data = response.json()
        if not data.get('success', False):
            raise Exception(f"Upload completion failed: {data.get('error', 'Unknown error')}")
        
        return data['result']
    
    def cancel_upload(self, session: UploadSession, databank_id: str) -> None:
        """Cancel multipart upload"""
        logger.info(f"Canceling upload: {session.key}")
        
        payload = {
            'key': session.key
        }
        
        response = self._make_request(
            'POST',
            f'databanks/{databank_id}/uploads/{session.upload_id}/cancel',
            json=payload
        )
        
        if response.status_code != 200:
            logger.warning(f"Failed to cancel upload: {response.status_code} - {response.text}")

# =============================================================================
# FILE UPLOADER
# =============================================================================

class FileUploader:
    """Handles file upload operations"""
    
    def __init__(self, client: DatabankAPIClient, databank_id: str):
        self.client = client
        self.databank_id = databank_id
    
    def upload_file(self, file_info: FileInfo) -> Dict:
        """Upload a single file"""
        logger.info(f"Starting upload: {file_info.relative_path} ({file_info.size} bytes)")
        
        # Calculate number of parts
        num_parts = calculate_file_parts(file_info.size, CHUNK_SIZE)
        
        # Initiate upload
        session = self.client.initiate_upload(
            key=file_info.relative_path,
            num_parts=num_parts,
            content_type=file_info.content_type,
            databank_id=self.databank_id
        )
        
        try:
            # Upload parts
            with open(file_info.path, 'rb') as f:
                for i, part in enumerate(session.parts):
                    # Read chunk
                    chunk = f.read(CHUNK_SIZE)
                    if not chunk:
                        break
                    
                    # Upload part
                    self.client.upload_part(part, chunk)
                    
                    # Progress
                    progress = (i + 1) / session.total_parts * 100
                    logger.info(f"Progress: {progress:.1f}% ({i + 1}/{session.total_parts} parts)")
            
            # Complete upload
            result = self.client.complete_upload(session, self.databank_id)
            
            logger.info(f"Upload completed successfully: {file_info.relative_path}")
            return result
            
        except Exception as e:
            logger.error(f"Upload failed: {e}")
            # Cancel upload on failure
            try:
                self.client.cancel_upload(session, self.databank_id)
            except Exception as cancel_error:
                logger.warning(f"Failed to cancel upload: {cancel_error}")
            raise
    
    def upload_files(self, files: List[FileInfo]) -> List[Dict]:
        """Upload multiple files"""
        results = []
        
        for i, file_info in enumerate(files):
            logger.info(f"Uploading file {i + 1}/{len(files)}: {file_info.relative_path}")
            
            try:
                result = self.upload_file(file_info)
                results.append({
                    'file': file_info.relative_path,
                    'success': True,
                    'result': result
                })
            except Exception as e:
                logger.error(f"Failed to upload {file_info.relative_path}: {e}")
                results.append({
                    'file': file_info.relative_path,
                    'success': False,
                    'error': str(e)
                })
        
        return results

# =============================================================================
# MAIN EXECUTION
# =============================================================================

def main():
    """Main execution function"""
    # Validate configuration
    if not API_BASE_URL or API_BASE_URL == "https://your-api-domain.com":
        raise ValueError("Please configure API_BASE_URL")
    
    if not DATABANK_ID or DATABANK_ID == "your-databank-id":
        raise ValueError("Please configure DATABANK_ID")
    
    if not FILE_PATH and not DIRECTORY_PATH:
        raise ValueError("Please configure either FILE_PATH or DIRECTORY_PATH")
    
    if FILE_PATH and DIRECTORY_PATH:
        raise ValueError("Configure either FILE_PATH or DIRECTORY_PATH, not both")
    
    # Initialize client
    client = DatabankAPIClient(API_BASE_URL, API_VERSION)
    
    # Authenticate
    client.authenticate()
    
    # Initialize uploader
    uploader = FileUploader(client, DATABANK_ID)
    
    # Collect files
    files = []
    if FILE_PATH:
        file_path = Path(FILE_PATH)
        file_info = get_file_info(file_path)
        files.append(file_info)
        logger.info(f"Single file upload: {file_info.relative_path}")
    else:
        directory_path = Path(DIRECTORY_PATH)
        files = collect_files_from_directory(directory_path)
        logger.info(f"Directory upload: {len(files)} files found")
    
    if not files:
        logger.warning("No files to upload")
        return
    
    # Upload files
    logger.info(f"Starting upload of {len(files)} files to databank {DATABANK_ID}")
    results = uploader.upload_files(files)
    
    # Summary
    successful = sum(1 for r in results if r['success'])
    failed = len(results) - successful
    
    logger.info(f"Upload completed: {successful} successful, {failed} failed")
    
    if failed > 0:
        logger.info("Failed uploads:")
        for result in results:
            if not result['success']:
                logger.info(f"  - {result['file']}: {result['error']}")

if __name__ == "__main__":
    try:
        main()
    except Exception as e:
        logger.error(f"Script failed: {e}")
        exit(1)`;

  copyCodeToClipboard(): void {
    navigator.clipboard.writeText(this.directPushCode).then(() => {
      this.toasterService.success('Code copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy code:', err);
      this.toasterService.error('Failed to copy code to clipboard');
    });
  }

  downloadCode(): void {
    const blob = new Blob([this.directPushCode], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'databank_uploader.py';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    this.toasterService.success('Code downloaded successfully!');
  }

  onSubmitDirectPush(): void {
    // Show success modal for Direct Push submission
    this.isUploadSuccessModalOpen = true;
  }

  async onSubmitSftpForm(): Promise<void> {
    if (this.sftpForm.valid) {
      // Call the same logic as onNext() for SFTP upload
      await this.onNext();
    } else {
      this.sftpForm.markAllAsTouched();
      this.toasterService.error('Please fill all required SFTP fields.');
    }
  }

  async onSubmitApiIntegrationForm(): Promise<void> {
    if (this.apiIntegrationForm.valid) {
      // Reuse stepper Publish logic for API Integration
      await this.onNext();
    } else {
      this.apiIntegrationForm.markAllAsTouched();
      this.toasterService.error('Please fill all required API Integration fields.');
    }
  }

  clearAllFiles(): void {
    this.selectedFiles = [];
    this.uploadedFiles = [];
  }

  /**
   * Upload more files - keeps existing uploaded files and allows adding new ones
   */
  uploadMoreFiles(): void {
    // Clear only the selected files, keep uploaded files
    this.selectedFiles = [];
    // Trigger file input click to select more files
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Upload a single file from selected files
   */
  async uploadSingleFileFromSelected(index: number): Promise<void> {
    if (this.uploading || index < 0 || index >= this.selectedFiles.length) {
      return;
    }

    const file = this.selectedFiles[index];

    // Create uploadedFile entry for this single file
    const fileItem: typeof this.uploadedFiles[0] = {
      file,
      progress: 0,
      status: 'pending'
    };

    // Add to uploadedFiles if not already there
    if (!this.uploadedFiles.find(f => f.file === file)) {
      this.uploadedFiles.push(fileItem);
    }

    // Remove from selectedFiles
    this.selectedFiles.splice(index, 1);

    // Upload the file
    this.uploading = true;
    await this.uploadSingleFile(fileItem);
    this.uploading = false;

    if (fileItem.status === 'completed') {
      this.toasterService.success('File uploaded successfully!');
    }
  }

  /**
   * Upload all files
   */
  async uploadAllFiles(): Promise<void> {
    if (this.uploading || this.selectedFiles.length === 0) {
      return;
    }

    console.log('uploadAllFiles called with selectedFiles:', this.selectedFiles.length);

    // Convert selectedFiles to uploadedFiles format
    // Add newly selected files to uploadedFiles array
    for (const file of this.selectedFiles) {
      // Check if file is not already in uploadedFiles
      const exists = this.uploadedFiles.some(f => this.getFilePath(f.file) === this.getFilePath(file) && f.file.size === file.size);
      if (!exists) {
        this.uploadedFiles.push({
          file,
          progress: 0,
          status: 'pending' as const
        });
      }
    }

    // Clear selectedFiles after moving to uploadedFiles
    this.selectedFiles = [];

    console.log('uploadedFiles after conversion:', this.uploadedFiles.length);

    this.uploading = true;
    const pendingFiles = this.uploadedFiles.filter(f => f.status === 'pending' || f.status === 'error');

    console.log('Pending files to upload:', pendingFiles.length);

    for (const fileItem of pendingFiles) {
      await this.uploadSingleFile(fileItem);
    }

    this.uploading = false;

    // Check if all files uploaded successfully
    const allSuccess = this.uploadedFiles.every(f => f.status === 'completed');
    if (allSuccess) {
      this.toasterService.success('All files uploaded successfully!');
    }
  }

  /**
   * Upload a single file using multipart upload (5-step process).
   * `storageFileName` overrides the file's own name as the S3 object name, so a file can be given a
   * standardized name regardless of what the user's local file is called. Regular dataset files
   * omit it.
   */
  private async uploadSingleFile(fileItem: typeof this.uploadedFiles[0], storageFileName?: string): Promise<void> {
    try {
      fileItem.status = 'uploading';
      fileItem.progress = 0;
      fileItem.error = undefined;

      // Get databankId from localStorage
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const formData = existingData ? JSON.parse(existingData) : {};
      const databankId = formData.itemId || formData.id || formData.item_id;

      if (!databankId) {
        throw new Error('Databank ID not found. Please complete previous steps first.');
      }

      const file = fileItem.file;
      const fileName = storageFileName ?? this.getFilePath(file);

      if (environment.encryptedDatasetUpload) {
        await this.uploadSingleFileEncrypted(fileItem, databankId, file, fileName);
        return;
      }

      console.log('Starting 5-step upload process for:', fileName);

      // ============================================
      // STEP 1: Initiate Upload
      // ============================================
      console.log('Step 1: Initiating upload...');
      const initiateResponse = await this.fileApiService.initiateUpload(
        databankId,
        fileName,
        file.type || 'application/octet-stream',
        1 // Single part upload
      );

      console.log('Step 1 Complete - Upload initiated:', initiateResponse);
      console.log('Response data structure:', JSON.stringify(initiateResponse.data, null, 2));

      // Handle nested response structure: { success: true, data: { uploadId, key, parts } }
      const responseData = initiateResponse.data.data || initiateResponse.data;
      const uploadId = responseData.uploadId;
      
      // The response structure has parts array with presignedUrl
      const presignedUrls = responseData.parts?.map((part: any) => part.presignedUrl) || [];

      console.log('Extracted uploadId:', uploadId);
      console.log('Extracted presigned URLs:', presignedUrls);

      if (!presignedUrls || presignedUrls.length === 0) {
        console.error('Full response:', initiateResponse);
        throw new Error('No presigned URLs received from server');
      }

      fileItem.progress = 10;

      // ============================================
      // STEP 2: Upload to S3/MinIO (Presigned URL)
      // ============================================
      console.log('Step 2: Uploading file to S3/MinIO...');
      const uploadResponse = await this.fileApiService.uploadPart(
        presignedUrls[0],
        file
      );

      console.log('Step 2 Complete - File uploaded to S3:', uploadResponse);

      const etag = uploadResponse.headers.get('ETag')?.replace(/"/g, '') || '';
      
      if (!etag) {
        throw new Error('No ETag received from S3 upload');
      }

      fileItem.progress = 40;

      // ============================================
      // STEP 3: Complete Upload
      // ============================================
      console.log('Step 3: Completing upload...');
      const completeResponse = await this.fileApiService.completeUpload(
        databankId,
        uploadId,
        fileName,
        [{ partNumber: 1, eTag: etag }]
      );

      console.log('Step 3 Complete - Upload completed:', completeResponse);
      fileItem.progress = 60;

      // ============================================
      // STEP 4: Refresh File List
      // ============================================
      console.log('Step 4: Refreshing file list...');
      const fileListResponse = await this.fileApiService.listFiles(
        databankId,
        1000,
        true
      );

      console.log('Step 4 Complete - File list refreshed:', fileListResponse);
      fileItem.progress = 80;

      // ============================================
      // STEP 5: Generate File Preview
      // ============================================
      console.log('Step 5: Generating file preview...');
      const previewResponse = await this.fileApiService.generatePreview(
        databankId,
        fileName
      );

      console.log('Step 5 Complete - Preview generated:', previewResponse);
      
      // Store preview data and upload metadata in the file item
      fileItem.previewData = previewResponse.data;
      fileItem.uploadId = uploadId;
      fileItem.key = fileName;
      
      fileItem.progress = 100;
      fileItem.status = 'completed';

      console.log('✅ All 5 steps completed successfully for:', fileName);
    } catch (error: any) {
      fileItem.status = 'error';
      fileItem.error = this.getUserFriendlyErrorMessage(error);
      console.error('Upload error:', error);
    }
  }

  /**
   * Encrypted upload path (environment.encryptedDatasetUpload): the file is
   * chunked and AES-256-GCM-encrypted in the browser, chunks are uploaded as
   * multipart parts, the fileserver composes them in order, and a manifest
   * sidecar (KMS-wrapped DEK + decryption parameters for the TEE) is stored
   * next to the object. Ciphertext cannot be previewed, so the preview step
   * is skipped.
   */
  private async uploadSingleFileEncrypted(
    fileItem: typeof this.uploadedFiles[0],
    databankId: string,
    file: File,
    fileName: string,
  ): Promise<void> {
    console.log('Starting encrypted chunked upload for:', fileName);

    // Chunk encryption + part uploads map to 0–85%.
    const manifest = await this.encryptedUploadService.uploadEncryptedFile(
      databankId,
      fileName,
      file,
      (pct) => { fileItem.progress = Math.round(pct * 0.85); },
    );
    console.log(`Encrypted upload composed (${manifest.total_chunks} chunks), manifest stored`);
    fileItem.progress = 90;

    await this.fileApiService.listFiles(databankId, 1000, true);

    // No preview: the stored object is ciphertext.
    fileItem.previewData = undefined;
    fileItem.key = fileName;
    fileItem.progress = 100;
    fileItem.status = 'completed';
    console.log('✅ Encrypted upload completed for:', fileName);
  }

  /**
   * Get user-friendly error message
   */
  private getUserFriendlyErrorMessage(error: any): string {
    const errorMessage = error?.message || error?.toString() || '';

    if (errorMessage.includes('0 Unknown Error') || errorMessage.includes('ERR_NETWORK')) {
      return 'Upload failed: Please check your internet connection and try again.';
    }
    if (errorMessage.includes('404')) {
      return 'Upload failed: Service not found. Please contact support.';
    }
    if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
      return 'Upload failed: Server error. Please try again later.';
    }
    if (errorMessage.includes('413') || errorMessage.includes('Payload Too Large')) {
      return 'Upload failed: File is too large. Please choose a smaller file.';
    }
    if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
      return 'Upload failed: You do not have permission to upload this file.';
    }
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
      return 'Upload failed: Please log in again and try.';
    }
    if (errorMessage.includes('timeout') || errorMessage.includes('TimeoutError')) {
      return 'Upload failed: Request timed out. Please try again.';
    }

    return 'Upload failed: Something went wrong. Please try again.';
  }

  closeUploadSuccessModal(): void {
    this.isUploadSuccessModalOpen = false;
    this.requestType = null;
    // Optionally clear files after successful upload
    // this.selectedFiles = [];
  }

  /**
   * Close Direct Push success modal
   */
  closeDirectPushSuccessModal(): void {
    this.isDirectPushSuccessModalOpen = false;
  }

  /**
   * Handle "View Model/Dataset" button click from Direct Push modal
   */
  onViewDatasetFromDirectPush(): void {
    this.closeDirectPushSuccessModal();
    
    if (this.createdDatasetId) {
      // Navigate to detail page based on type
      if (this.isModel()) {
        this.router.navigate(['/model', this.createdDatasetId]);
      } else {
        this.router.navigate(['/dataset', this.createdDatasetId]);
      }
    } else {
      console.warn('Item ID not available for navigation');
      // Fallback: navigate to list page based on type
      if (this.isModel()) {
        this.router.navigate(['/models']);
      } else {
        this.router.navigate(['/datasets']);
      }
    }
  }

  /**
   * Handle "Create New" button click from Direct Push modal
   */
  onCreateNewFromDirectPush(): void {
    this.closeDirectPushSuccessModal();

    // Clear all flow state so Step 1 opens completely fresh
    const localStorageKey = this.getLocalStorageKey();
    localStorage.removeItem(localStorageKey);
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('updateBaseline:datasetFormData') || k.startsWith('updateBaseline:modelFormData'))) {
        sessionStorage.removeItem(k);
      }
    }

    this.createdDatasetId = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;

    if (currentUrl.includes('/org-admin/')) {
      navigateRoute = currentUrl.includes('/add-model/') ? '/org-admin/add-model' : '/org-admin/add-dataset';
    } else {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/provider/add-contributions/add-model'
        : '/provider/add-contributions/add-dataset';
    }

    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  /**
   * Get the request type name for display
   */
  get requestTypeName(): string {
    if (this.requestType === 'SFTP') {
      return 'SFTP request';
    } else if (this.requestType === 'API') {
      return 'API integration';
    }
    return 'request';
  }

  /**
   * Get success title based on type (model or dataset) and role (provider vs org-admin).
   * Provider-created assets require Organisation Admin approval before becoming available.
   */
  getSuccessTitle(): string {
    if (this.isOrgAdminRoute) {
      return this.isModel() ? 'Model Created successfully' : 'Dataset Created Successfully';
    }
    return this.isModel() ? 'Model Submitted for Approval' : 'Dataset Submitted for Approval';
  }

  /**
   * Get success message based on type (model or dataset) and role (provider vs org-admin).
   * Provider-created assets require Organisation Admin approval before becoming available.
   */
  getSuccessMessage(): string {
    if (this.isOrgAdminRoute) {
      return this.isModel()
        ? 'Your model has been created and is ready to use.'
        : 'Your dataset has been created and is ready to use.';
    }
    return this.isModel()
      ? 'Your model request has been submitted and is awaiting Organisation Admin approval.'
      : 'Your dataset request has been submitted and is awaiting Organisation Admin approval.';
  }

  /**
   * Get view button label based on type (model or dataset)
   */
  getViewButtonLabel(): string {
    return this.isModel() ? 'View Model' : 'View Dataset';
  }

  /**
   * Get the next steps message for the confirmation dialog
   */
  getNextStepsMessage(): string {
    if (this.requestType === 'SFTP') {
      return 'Our team will review your SFTP configuration details. You\'ll receive an email notification once your SFTP access is approved and set up.';
    } else if (this.requestType === 'API') {
      return 'Our team will review your API integration details. You\'ll receive an email notification once your API integration is approved and set up.';
    }
    return '';
  }

  /**
   * Handle "View Dataset/Model" button click from request success modal
   */
  onViewDatasetFromRequest(): void {
    this.closeUploadSuccessModal();
    
    if (this.createdDatasetId) {
      // Navigate to detail page based on type
      if (this.isModel()) {
        // Navigate to model detail page (adjust route as needed)
        this.router.navigate(['/model', this.createdDatasetId]);
      } else {
        // Navigate to dataset detail page
        this.router.navigate(['/dataset', this.createdDatasetId]);
      }
    } else {
      console.warn('Item ID not available for navigation');
      // Fallback: navigate to list page based on type
      if (this.isModel()) {
        this.router.navigate(['/models']);
      } else {
        this.router.navigate(['/datasets']);
      }
    }
  }

  /**
   * Handle "Create New" button click from request success modal
   */
  onCreateNewFromRequest(): void {
    this.closeUploadSuccessModal();

    // Clear all flow state so Step 1 opens completely fresh
    const localStorageKey = this.getLocalStorageKey();
    localStorage.removeItem(localStorageKey);
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('updateBaseline:datasetFormData') || k.startsWith('updateBaseline:modelFormData'))) {
        sessionStorage.removeItem(k);
      }
    }

    this.createdDatasetId = null;
    this.requestType = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;

    if (currentUrl.includes('/org-admin/')) {
      navigateRoute = currentUrl.includes('/add-model/') ? '/org-admin/add-model' : '/org-admin/add-dataset';
    } else {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/provider/add-contributions/add-model'
        : '/provider/add-contributions/add-dataset';
    }

    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  /**
   * Open file preview modal
   */
  openFilePreview(fileItem: typeof this.uploadedFiles[0]): void {
    if (fileItem.previewData) {
      this.previewingFile = {
        name: this.getFilePath(fileItem.file),
        size: fileItem.file.size,
        type: fileItem.file.type,
        lastModified: fileItem.file.lastModified,
        previewData: fileItem.previewData,
        parsedData: this.parsePreviewData(fileItem.previewData, this.getFilePath(fileItem.file))
      };
      this.previewModalOpen = true;
    } else {
      this.toasterService.error('Preview not available for this file');
    }
  }

  /**
   * Parse preview data into table format
   */
  parsePreviewData(previewData: any, fileName: string): { headers: string[], rows: any[][] } | null {
    try {
      // Check if it's CSV data (string format)
      if (typeof previewData === 'string') {
        return this.parseCSV(previewData);
      }
      
      // Handle nested API response structure: { success: true, data: { content: { data: [...] } } }
      if (previewData.success && previewData.data?.content?.data) {
        const data = previewData.data.content.data;
        if (Array.isArray(data) && data.length > 0) {
          const headers = Object.keys(data[0]);
          const rows = data.map((row: any) => Object.values(row));
          return { headers, rows };
        }
      }
      
      // Handle nested structure: { data: { content: { data: [...] } } }
      if (previewData.data?.content?.data) {
        const data = previewData.data.content.data;
        if (Array.isArray(data) && data.length > 0) {
          const headers = Object.keys(data[0]);
          const rows = data.map((row: any) => Object.values(row));
          return { headers, rows };
        }
      }
      
      // Handle structure: { content: { data: [...] } }
      if (previewData.content?.data) {
        const data = previewData.content.data;
        if (Array.isArray(data) && data.length > 0) {
          const headers = Object.keys(data[0]);
          const rows = data.map((row: any) => Object.values(row));
          return { headers, rows };
        }
      }
      
      // Check if it's already parsed data with rows
      if (previewData.data && Array.isArray(previewData.data)) {
        const data = previewData.data;
        if (data.length === 0) return null;
        
        // Extract headers from first row or object keys
        const headers = typeof data[0] === 'object' && !Array.isArray(data[0])
          ? Object.keys(data[0])
          : data[0];
        
        // Extract rows
        const rows = typeof data[0] === 'object' && !Array.isArray(data[0])
          ? data.map((row: any) => Object.values(row))
          : data.slice(1);
        
        return { headers, rows };
      }
      
      // Check if it's an array of objects
      if (Array.isArray(previewData) && previewData.length > 0) {
        const headers = Object.keys(previewData[0]);
        const rows = previewData.map((row: any) => Object.values(row));
        return { headers, rows };
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing preview data:', error);
      return null;
    }
  }

  /**
   * Parse CSV string into table format
   */
  parseCSV(csvString: string): { headers: string[], rows: any[][] } {
    const lines = csvString.trim().split('\n');
    if (lines.length === 0) return { headers: [], rows: [] };
    
    // Parse CSV line considering quoted values
    const parseLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };
    
    const headers = parseLine(lines[0]);
    const rows = lines.slice(1).map(line => parseLine(line));
    
    return { headers, rows };
  }

  /**
   * Close file preview modal
   */
  closePreview(): void {
    this.previewModalOpen = false;
    this.previewingFile = null;
  }

  /**
   * Check if all uploaded files are completed
   */
  isAllCompleted(): boolean {
    return this.uploadedFiles.length > 0 && 
           this.uploadedFiles.every(f => f.status === 'completed');
  }

  // Dropdown methods for API Integration
  toggleHttpMethodDropdown(): void {
    this.isHttpMethodDropdownOpen = !this.isHttpMethodDropdownOpen;
    this.isAuthenticationTypeDropdownOpen = false;
    this.isResponseFormatDropdownOpen = false;
  }

  selectHttpMethod(method: string): void {
    this.apiIntegrationForm.patchValue({ httpMethod: method });
    this.isHttpMethodDropdownOpen = false;
    this.apiIntegrationForm.get('httpMethod')?.markAsTouched();
  }

  getSelectedHttpMethod(): string {
    return this.apiIntegrationForm.get('httpMethod')?.value || 'Select HTTP Method';
  }

  isHttpMethodSelected(method: string): boolean {
    return this.apiIntegrationForm.get('httpMethod')?.value === method;
  }

  toggleAuthenticationTypeDropdown(): void {
    this.isAuthenticationTypeDropdownOpen = !this.isAuthenticationTypeDropdownOpen;
    this.isHttpMethodDropdownOpen = false;
    this.isResponseFormatDropdownOpen = false;
  }

  selectAuthenticationType(type: string): void {
    this.apiIntegrationForm.patchValue({ authenticationType: type });
    this.isAuthenticationTypeDropdownOpen = false;
    this.apiIntegrationForm.get('authenticationType')?.markAsTouched();
  }

  getSelectedAuthenticationType(): string {
    return this.apiIntegrationForm.get('authenticationType')?.value || 'Select Authentication Type';
  }

  isAuthenticationTypeSelected(type: string): boolean {
    return this.apiIntegrationForm.get('authenticationType')?.value === type;
  }

  toggleResponseFormatDropdown(): void {
    this.isResponseFormatDropdownOpen = !this.isResponseFormatDropdownOpen;
    this.isHttpMethodDropdownOpen = false;
    this.isAuthenticationTypeDropdownOpen = false;
  }

  selectResponseFormat(format: string): void {
    this.apiIntegrationForm.patchValue({ responseFormat: format });
    this.isResponseFormatDropdownOpen = false;
    this.apiIntegrationForm.get('responseFormat')?.markAsTouched();
  }

  getSelectedResponseFormat(): string {
    return this.apiIntegrationForm.get('responseFormat')?.value || 'Select Response Format';
  }

  isResponseFormatSelected(format: string): boolean {
    return this.apiIntegrationForm.get('responseFormat')?.value === format;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.isHttpMethodDropdownOpen = false;
    this.isAuthenticationTypeDropdownOpen = false;
    this.isResponseFormatDropdownOpen = false;
  }

  goBack(): void {
    const isUploadMetadataFlow = this.isUploadMetadataFlow();

    // Navigate to previous step based on flow
    if (isUploadMetadataFlow) {
      this.router.navigate(['../preview-metadata'], { relativeTo: this.route });
    } else if (this.isModel()) {
      sessionStorage.setItem('cameFromUploadsStep', 'true');
      this.router.navigate(['../additionalInfo'], { relativeTo: this.route });
    } else {
      this.router.navigate(['../additionalInfo'], { relativeTo: this.route });
    }
  }

  ngAfterViewInit(): void {
    const stepIndex = this.getUploadStepContentIndex();
    if (this.step4Template && this.stepperSteps[stepIndex]) {
      this.stepperSteps[stepIndex].content = this.step4Template;
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    const routeBase = this.getRouteBase();
    const isUploadMetadataFlow = this.isUploadMetadataFlow();

    // Navigate to the appropriate route based on step index and flow
    let routes: string[];
    if (isUploadMetadataFlow) {
      routes = [
        routeBase,
        `${routeBase}/upload-metadata`,
        `${routeBase}/preview-metadata`,
        `${routeBase}/uploads`
      ];
    } else if (this.isModel()) {
      routes = [
        routeBase,
        `${routeBase}/metadata`,
        `${routeBase}/additionalInfo`,
        `${routeBase}/uploads`
      ];
    } else {
      routes = [
        routeBase,
        `${routeBase}/metadata`,
        `${routeBase}/additionalInfo`,
        `${routeBase}/uploads`
      ];
    }
    
    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  async onNext(): Promise<void> {
    console.log('onNext() called in upload component - Publish button clicked');
    
    // Check if already creating to prevent duplicate calls
    if (this.isCreatingItem) {
      console.warn('Already creating item, skipping duplicate call');
      return;
    }

    // Get form data to determine update flow before try block
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const formData = existingData ? JSON.parse(existingData) : {};
    let itemId: string | null = formData.itemId || formData.id || formData.item_id || null;
    const isModel = this.router.url.includes('/add-model/');

    try {
      console.log('Starting upload-step publish...');
      this.isCreatingItem = true;

      console.log('Form data from localStorage:', formData);
      console.log('Item ID:', itemId);

      // Ensure we have an itemId (if user landed on uploads without creating item earlier)
      if (!itemId) {
        console.log('No itemId found; creating item before upload request...');
        const itemPayload = await this.buildCreateAssetPayload(formData);
        const response = await this.catalogueApiService.createAssetItem(itemPayload);
        const createdItemId = extractCreatedItemId(response);

        if (createdItemId) {
          itemId = createdItemId;
          console.log('Storing item ID in localStorage:', createdItemId);
          formData.itemId = createdItemId;
          formData.id = createdItemId;
          localStorage.setItem(localStorageKey, JSON.stringify(formData));
          // Store the created item ID for navigation (for both datasets and models)
          this.createdDatasetId = createdItemId;
        } else {
          console.warn('Item ID not found in create response:', response);
        }
      }

      // If SFTP upload selected, save SFTP config via createDataUploadRequest
      if (this.isSftpUploadSelected()) {
        if (this.sftpForm.invalid) {
          Object.keys(this.sftpForm.controls).forEach(key => {
            this.sftpForm.get(key)?.markAsTouched();
          });
          this.toasterService.error('Please fill all required SFTP fields.');
          return;
        }

        const sftpPayload: any = {
          type: 'SFTP',
          additional_info: {
            sftpHost: this.sftpForm.value.sftpHost || '',
            sftpPort: this.sftpForm.value.port || '',
            sftpUser: this.sftpForm.value.username || '',
            sftpPass: this.sftpForm.value.password || '',
            remotePath: this.sftpForm.value.remotePath || '',
            localPath: this.sftpForm.value.localFilePath || '',
            description: this.sftpForm.value.optionalNotes || ''
          },
          asset_id: itemId
        };

        console.log('Calling createDataUploadRequest for SFTP:', sftpPayload);
        const sftpResponse = await this.authApiService.createDataUploadRequest(sftpPayload);
        console.log('SFTP upload request created successfully:', sftpResponse);
        
        // Store request type and dataset ID for success modal
        this.requestType = 'SFTP';
        if (itemId) {
          this.createdDatasetId = itemId;
        }
      } else if (this.isApiIntegrationSelected()) {
        // If API Integration selected, save API config via createDataUploadRequest
        if (this.apiIntegrationForm.invalid) {
          Object.keys(this.apiIntegrationForm.controls).forEach(key => {
            this.apiIntegrationForm.get(key)?.markAsTouched();
          });
          this.toasterService.error('Please fill all required API Integration fields.');
          return;
        }

        const apiPayload: any = {
          type: 'API',
          additional_info: {
            sourceName: this.apiIntegrationForm.value.dataSourceName || '',
            apiBaseUrl: this.apiIntegrationForm.value.apiBaseUrl || '',
            endpointPath: this.apiIntegrationForm.value.endpointPath || '',
            httpMethod: this.apiIntegrationForm.value.httpMethod || '',
            headers: this.apiIntegrationForm.value.customHeaders || '',
            queryParams: this.apiIntegrationForm.value.queryParameters || '',
            bodyPayload: this.apiIntegrationForm.value.bodyPayload || '',
            authDetails: this.apiIntegrationForm.value.authenticationDetails || '',
            authType: this.apiIntegrationForm.value.authenticationType || '',
            responseFormat: this.apiIntegrationForm.value.responseFormat || '',
            dataPath: this.apiIntegrationForm.value.dataPathInResponse || '',
            schedule: this.apiIntegrationForm.value.expectedPullFrequency || '',
            notes: this.apiIntegrationForm.value.additionalNotes || '',
          },
          asset_id: itemId,
        };

        console.log('Calling createDataUploadRequest for API Integration:', apiPayload);
        const apiResponse = await this.authApiService.createDataUploadRequest(apiPayload);
        console.log('API Integration upload request created successfully:', apiResponse);

        // Store request type and dataset ID for success modal
        this.requestType = 'API';
        if (itemId) {
          this.createdDatasetId = itemId;
        }
      } else if (this.isUploadFileSelected() && this.uploadedFiles.length > 0) {
        // If Upload File selected and files are uploaded, call process API
        console.log('Calling process API for uploaded files...');
        await this.publishDatabank();
        // publishDatabank() will show the success modal, so return early
        return;
      } else if (this.isDirectPushSelected()) {
        // If Direct Push selected, show Direct Push success modal
        console.log('Direct Push selected - showing success modal');
        this.isDirectPushSuccessModalOpen = true;
        return;
      }

      // Show success modal for SFTP/API Integration
      this.isUploadSuccessModalOpen = true;
    } catch (error: any) {
      console.error('Error on upload-step publish:', error);
      
      const errorMessage = isModel
        ? 'Unable to process upload request for Model, Please try again later !!'
        : 'Unable to process upload request for Dataset, Please try again later !!';
      
      // Show error toaster
      this.toasterService.error(errorMessage);
    } finally {
      this.isCreatingItem = false;
    }
  }

  /**
   * Build payload for createAssetItem API or updateItem API
   */
  private async buildCreateAssetPayload(formData: any): Promise<any> {
    // Check if this is a model or dataset
    const isModel = this.router.url.includes('/add-model/');
    
    // Get user and organization info
    let userData: any = null;
    let organizationsList: any[] = [];
    
    try {
      userData = await this.authApiService.getUserInfo();
      // Get all organizations to find the matching one
      const orgsResponse = await this.authApiService.getAllOrganisations();
      organizationsList = orgsResponse?.result || orgsResponse?.data || [];
    } catch (error) {
      console.error('Error fetching user/organization info:', error);
    }

    // Extract organization info from userData
    const result = userData?.result || userData || {};
    const organisation = result.organisation || {};
    const userOrganisationId = organisation.organisationId || organisation.id || '';
    
    // Find matching organization from the list
    let organisationName = '';
    let organisationType = 'Public';
    let industry = 'forest';
    let department = '';
    
    if (userOrganisationId && organizationsList.length > 0) {
      const matchedOrg = organizationsList.find(
        (org: any) => (org.id === userOrganisationId || org.organisationId === userOrganisationId)
      );
      if (matchedOrg) {
        organisationName = matchedOrg.orgName || matchedOrg.name || '';
        organisationType = matchedOrg.organisationType || matchedOrg.type || 'Public';
        industry = matchedOrg.industry || 'forest';
        department = matchedOrg.department || matchedOrg.dept || matchedOrg.departmentName || '';
      }
    }
    
    // Get user email for uploadedBy (prefer email over userName)
    const uploadedBy = result.email || result.emailAddress || result.registeredEmail || result.userName || result.name || '';

    // Build payload based on type (model or dataset)
    if (isModel) {
      // Build model payload
      const accessPolicy = formData.permission === 'Restricted' ? 'PRIVATE' : 'OPEN';
      
      // Ensure tags is an array
      let tagsArray: string[] = [];
      if (Array.isArray(formData.tags)) {
        tagsArray = formData.tags;
      } else if (formData.tags) {
        tagsArray = typeof formData.tags === 'string' 
          ? formData.tags.split(',').map((t: string) => t.trim()).filter((t: string) => t.length > 0)
          : [formData.tags];
      }
      
      // Extract descriptions from formData or jsonSchema
      let shortDescription = formData.shortDescription || '';
      let longDescription = formData.longDescription || formData.description || '';
      
      // If descriptions are not in formData, check jsonSchema (for upload-metadata flow)
      if (!shortDescription || !longDescription) {
        try {
          const jsonSchema = formData.jsonSchema;
          if (jsonSchema) {
            const parsedSchema = typeof jsonSchema === 'string' ? JSON.parse(jsonSchema) : jsonSchema;
            shortDescription = shortDescription || parsedSchema.shortDescription || '';
            longDescription = longDescription || parsedSchema.longDescription || parsedSchema.description || '';
          }
        } catch (error) {
          console.warn('Error parsing jsonSchema for descriptions:', error);
        }
      }
      
      const payload: any = {
        type: ['adex:AiModel'],
        name: formData.title || '',
        description: longDescription,
        shortDescription: shortDescription,
        label: formData.title || '',
        tags: tagsArray,
        accessPolicy: accessPolicy,
        organizationId: userOrganisationId || '',
        organizationType: organisationType || 'Public',
        department: department || '',
        modelType: formData.datasetType || '',
        fileFormat: (formData.fileFormat || '').toLowerCase(),
        industry: industry || 'Agriculture',
        uploadedBy: uploadedBy,
        license: formData.license || 'MIT',
        fileSize: '15MB',
        mediaURL: formData.sampleDataUrl || 'https://iudx.io/'
      };

      console.log('Model payload built:', JSON.stringify(payload, null, 2));
      return payload;
    }

    return buildTanuhDatasetItemPayload(formData, this.authApiService);
  }

  /**
   * Remove a file from the databank on the server:
   * - For completed uploads: calls delete file API
   * - For in-progress/initiated uploads: calls cancel upload API
   * - For pending uploads (never sent to server): no-op
   */
  private async removeFileFromServer(fileItem: typeof this.uploadedFiles[0]): Promise<void> {
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const formData = existingData ? JSON.parse(existingData) : {};
    const databankId = formData.itemId || formData.id || formData.item_id;

    if (!databankId) {
      throw new Error('Databank ID not found');
    }

    if (fileItem.status === 'completed' && fileItem.key) {
      console.log('Deleting completed file from S3:', fileItem.key);
      await this.fileApiService.deleteFile(databankId, fileItem.key);
    } else if (fileItem.uploadId && fileItem.key) {
      console.log('Cancelling upload:', fileItem.uploadId);
      await this.fileApiService.cancelUpload(databankId, fileItem.uploadId, fileItem.key);
    } else {
      console.log('Removing pending file from list');
    }
  }

  /**
   * Delete an uploaded file
   */
  async deleteUploadedFile(fileItem: typeof this.uploadedFiles[0]): Promise<void> {
    try {
      await this.removeFileFromServer(fileItem);

      // Remove from list
      this.uploadedFiles = this.uploadedFiles.filter(f => f !== fileItem);

      // Clear preview if this was the previewed file
      if (this.publishedFilesPreview) {
        this.publishedFilesPreview = null;
      }

      this.toasterService.success('File deleted successfully');
    } catch (error: any) {
      console.error('Error deleting file:', error);
      this.toasterService.error('Failed to delete file: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Handle Ground Truth file selection (.json or .csv). Single file - replaces any previous selection.
   */
  onGroundTruthSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      const ext = this.getFileExtension(file.name);
      if (ext !== '.json' && ext !== '.csv') {
        this.toasterService.error('Only .json or .csv files can be uploaded for Ground Truth.');
      } else {
        this.groundTruthItem = { file, progress: 0, status: 'pending' };
      }
    }
    input.value = '';
  }

  /**
   * Upload the selected Ground Truth file using the same multipart upload flow (same databankId).
   */
  async uploadGroundTruthFile(): Promise<void> {
    if (!this.groundTruthItem || this.groundTruthItem.status === 'uploading') {
      return;
    }
    await this.uploadSingleFile(this.groundTruthItem);
    if (this.groundTruthItem.status === 'completed') {
      this.toasterService.success('Ground truth file uploaded successfully!');
    }
  }

  /**
   * Remove the Ground Truth file (cancels/deletes on server if already sent).
   */
  async deleteGroundTruthFile(): Promise<void> {
    if (!this.groundTruthItem) {
      return;
    }
    try {
      await this.removeFileFromServer(this.groundTruthItem);
      this.groundTruthItem = null;
      this.toasterService.success('Ground truth file removed successfully');
    } catch (error: any) {
      console.error('Error removing ground truth file:', error);
      this.toasterService.error('Failed to remove file: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Publish/Process the databank
   */
  async publishDatabank(): Promise<void> {
    if (this.groundTruthItem?.status !== 'completed') {
      this.toasterService.error('Please upload the ground truth file before publishing.');
      return;
    }
    try {
      this.isPublishing = true;

      // Get databankId
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const formData = existingData ? JSON.parse(existingData) : {};
      const databankId = formData.itemId || formData.id || formData.item_id;

      if (!databankId) {
        throw new Error('Databank ID not found');
      }

      // Call process API
      const response = await this.fileApiService.processDatabank(databankId, 'all');

      console.log('Databank processed successfully:', response);

      // Check if response status is 200 OK or 202 Accepted
      if (response.status === 200 || response.status === 202) {
        // Mark as published
        this.isPublished = true;
        
        // Store the dataset/model ID for navigation if not already stored
        if (!this.createdDatasetId) {
          const localStorageKey = this.getLocalStorageKey();
          const existingData = localStorage.getItem(localStorageKey);
          const formData = existingData ? JSON.parse(existingData) : {};
          const databankId = formData.itemId || formData.id || formData.item_id;
          if (databankId) {
            this.createdDatasetId = databankId;
          }
        }
        
        // Show success modal on 200 OK or 202 Accepted
        this.isPublishSuccessModalOpen = true;
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error publishing databank:', error);
      this.toasterService.error('Failed to publish: ' + (error.message || 'Unknown error'));
    } finally {
      this.isPublishing = false;
    }
  }

  /**
   * Load preview for the first completed file
   */
  loadPublishedFilesPreview(): void {
    const completedFile = this.uploadedFiles.find(f => f.status === 'completed' && f.previewData);
    
    if (completedFile && completedFile.previewData) {
      this.publishedFilesPreview = this.parsePreviewData(
        completedFile.previewData,
        this.getFilePath(completedFile.file)
      );
    }
  }

  /**
   * Close publish success modal - stay on current step
   */
  closePublishSuccessModal(): void {
    this.isPublishSuccessModalOpen = false;
    // User stays on Step 4 (Upload step) after closing modal
  }

  /**
   * Handle "View Dataset/Model" button click - navigate to the created item detail page
   */
  onViewDataset(): void {
    this.closePublishSuccessModal();
    
    if (this.createdDatasetId) {
      // Navigate to detail page based on type
      if (this.isModel()) {
        // Navigate to model detail page (adjust route as needed)
        this.router.navigate(['/model', this.createdDatasetId]);
      } else {
        // Navigate to dataset detail page
        this.router.navigate(['/dataset', this.createdDatasetId]);
      }
    } else {
      console.warn('Item ID not available for navigation');
      // Fallback: navigate to list page based on type
      if (this.isModel()) {
        this.router.navigate(['/models']);
      } else {
        this.router.navigate(['/datasets']);
      }
    }
  }

  /**
   * Handle "Create New" button click - reset form and navigate to step 1
   */
  onCreateNew(): void {
    this.closePublishSuccessModal();

    // Clear all flow state so Step 1 opens completely fresh
    const localStorageKey = this.getLocalStorageKey();
    localStorage.removeItem(localStorageKey);
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('updateBaseline:datasetFormData') || k.startsWith('updateBaseline:modelFormData'))) {
        sessionStorage.removeItem(k);
      }
    }

    this.createdDatasetId = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;

    if (currentUrl.includes('/org-admin/')) {
      navigateRoute = currentUrl.includes('/add-model/') ? '/org-admin/add-model' : '/org-admin/add-dataset';
    } else {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/provider/add-contributions/add-model'
        : '/provider/add-contributions/add-dataset';
    }

    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  /**
   * Navigate to dashboard after successful publish (kept for backward compatibility)
   */
  navigateToDashboard(): void {
    this.isPublishSuccessModalOpen = false;
    this.router.navigate(['/provider/dashboard']);
  }

  onPrevious(): void {
    this.goBack();
  }
}

